import crossfilter2 from 'crossfilter2';
import {
  maxBy,
  map,
  filter,
  find,
  get,
  difference,
  assignIn,
  keyBy,
  uniq,
} from 'lodash';

export const usersCf = crossfilter2([]);

export const segmentsDimAsArrays = usersCf.dimension(d => d._segments);
export const presentEventsDimAsArrays = usersCf.dimension(
  d => d._presentEvents
);
export const presentMomentsDimAsArrays = usersCf.dimension(
  d => d._presentMoments
);

export const maxFlushTimeThreshold = 15;
export const maxFlushCountThreshold = 100;

const now = Date.now();

/**
 * Applies any filters present on the state.
 */
export const applyFilters = filters => {
  const events = getFiltersForType(filters, 'events');
  const moments = getFiltersForType(filters, 'moments');
  const segments = getFiltersForType(filters, 'segments');

  if (events.length > 0) {
    presentEventsDimAsArrays.filter(d => difference(events, d).length === 0);
  }

  if (moments.length > 0) {
    presentMomentsDimAsArrays.filter(d => difference(moments, d).length === 0);
  }

  if (segments.length > 0) {
    segmentsDimAsArrays.filter(d => difference(segments, d).length === 0);
  }
};

/**
 * Resets filters on all dimensions.
 */
export const resetFilters = () => {
  presentEventsDimAsArrays.filterAll();
  presentMomentsDimAsArrays.filterAll();
  segmentsDimAsArrays.filterAll();
};

/**
 * Add flat context arrays to user for easier dimension filtering
 *
 * @param {Object} user
 * @returns
 */
export const getDimensions = user => {
  const { segments = [], event_history = [], moment_history = [] } = user;

  const prediction_tree = get(user, 'prediction_tree.events', []);
  const _segments = map(segments, segment => segment.segment_definition_id);
  const _pastEvents = getPastEvents(event_history);
  const _presentEvents = getPresentEvents(event_history);
  const _futureEvents = getFutureEvents(prediction_tree);
  const _pastMoments =
    event_history.length > 0 ? getPastMoments(moment_history) : [];
  const _presentMoments =
    event_history.length > 0 ? getPresentMoments(moment_history) : [];

  const _presentEventType = getPresentEventType(event_history);

  return {
    _segments,
    _pastEvents,
    _presentEvents,
    _futureEvents,
    _pastMoments,
    _presentMoments,
    _presentEventType,
  };
};

/**
 * Filter batched users from Firehose on event or moment updates
 *
 * @param {Array<Object>} prevUsers
 * @param {Array<Object>} currUsers
 * @param {Array<Object>} newUsers
 * @returns Array of users with context update
 */
export const getUsersWithContextUpdate = (prevUsers, currUsers, newUsers) => {
  let usersWithContextUpdate = map(newUsers, user => {
    const prevUser = prevUsers[user.id];
    const currUser = currUsers[user.id];

    if (!prevUser || !currUser) {
      return user;
    }

    const _eventUpdates = difference(
      user._presentEvents,
      prevUser._presentEvents
    );
    const _momentUpdates = difference(
      user._presentMoments,
      prevUser._presentMoments
    );
    const _updateDelay = Math.random() * maxFlushCountThreshold * 1000;

    return assignIn({}, user, { _eventUpdates, _momentUpdates, _updateDelay });
  });

  usersWithContextUpdate = filter(usersWithContextUpdate, user => {
    const { _eventUpdates, _momentUpdates } = user;

    if (_eventUpdates && _eventUpdates.length > 0) {
      return true;
    }

    if (_momentUpdates && _momentUpdates.length > 0) {
      return true;
    }

    return false;
  });

  return usersWithContextUpdate;
};

const getPastEvents = eventHistory => {
  eventHistory = filter(eventHistory, item => {
    if (item.type === 'Transport') {
      return item.mode !== 'insufficient_data';
    }

    return true;
  });

  const lastEvent = maxBy(eventHistory, 'end_ts');

  if (!lastEvent) {
    return [];
  }

  eventHistory = filter(eventHistory, item => item.end_ts === lastEvent.end_ts);
  eventHistory = map(eventHistory, item =>
    item.type === 'Stationary' ? get(item, 'location.significance') : item.mode
  );

  return filter(eventHistory);
};

const getPresentEvents = eventHistory => {
  eventHistory = filter(eventHistory, item => {
    if (isLongerThan24h(item.start_ts, now)) {
      return false;
    }

    if (item.type === 'Transport') {
      return item.end_ts === null && item.mode !== 'insufficient_data';
    }

    return item.end_ts === null;
  });
  eventHistory = map(eventHistory, item =>
    item.type === 'Stationary' ? get(item, 'location.significance') : item.mode
  );

  return filter(eventHistory);
};

const getFutureEvents = predictions => {
  predictions = map(predictions, item =>
    item.type === 'StationaryPrediction'
      ? get(item, 'location_type')
      : item.mode
  );

  return filter(uniq(predictions));
};

const getPastMoments = momentHistory => {
  const pastMoment = maxBy(momentHistory, 'end_ts');

  if (!pastMoment) {
    return [];
  }

  const maxEndTs = pastMoment.end_ts;

  momentHistory = filter(
    momentHistory,
    item => item.end_ts === maxEndTs && item.type === 'GenericMoment'
  );
  momentHistory = map(momentHistory, item => item.moment_definition_id);

  return momentHistory;
};

const getPresentMoments = momentHistory => {
  momentHistory = filter(
    momentHistory,
    item =>
      item.end === null &&
      item.type === 'GenericMoment' &&
      !isLongerThan24h(item.start_ts, now)
  );
  momentHistory = map(momentHistory, item => item.moment_definition_id);

  return momentHistory;
};

const getPresentEventType = eventHistory => {
  const presentEvent = find(eventHistory, item => item.end === null);

  return get(presentEvent, 'type') || 'Stationary';
};

const getFiltersForType = (filters, type) => {
  let items = filter(filters, item => item.type === type);

  return map(items, item => item.id);
};

const isLongerThan24h = (startTs, endTs) => {
  return endTs - startTs > 86400000;
};
